import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCurrentCompany } from './useCurrentCompany';

export interface GoogleBusinessSummary {
  id: string;
  company_id: string;
  location_id: string | null;
  place_id: string;
  business_name: string | null;
  overall_rating: number | null;
  total_reviews: number;
  address: string | null;
  phone_number: string | null;
  website_url: string | null;
  business_status: string | null;
  last_fetched_at: string | null;
}

export function useGoogleBusinessSummary() {
  const { data: companyId } = useCurrentCompany();

  return useQuery({
    queryKey: ['google-business-summary', companyId],
    queryFn: async () => {
      if (!companyId) return null;

      try {
        const { data, error } = await (supabase as any)
          .from('google_business_summary')
          .select('*')
          .eq('company_id', companyId)
          .maybeSingle();

        if (error) {
          // If table doesn't exist (migration not applied), return null gracefully
          if (error.code === 'PGRST116' || error.message?.includes('does not exist')) {
            console.warn('Google business summary table not found. Migrations may not be applied.');
            return null;
          }
          console.error('Error fetching Google business summary:', error);
          throw error;
        }

        return data as GoogleBusinessSummary | null;
      } catch (error: any) {
        // Handle table not found gracefully
        if (error?.code === 'PGRST116' || error?.message?.includes('does not exist')) {
          console.warn('Google business summary table not found. Migrations may not be applied.');
          return null;
        }
        throw error;
      }
    },
    enabled: !!companyId,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}
